<script lang="ts">
  // https://www.fullstacklabs.co/blog/creating-an-svg-gauge-component-from-scratch
  import Stars from './Stars.svelte';
  import Handle from '../Handle.svelte';
  import { interpolateRgb } from 'd3-interpolate';
  import Marquee from '../Marquee.svelte';
  import { Tween } from 'svelte/motion';
  import { backOut } from 'svelte/easing';
  import { dims } from '~/utils/dimensions.svelte';
  import GaugeContainer from '../GaugeContainer.svelte';
  import { theme } from '~/theme/darkMode.svelte';
  import type { Direction } from '~/animations.svelte';
  import { getGame } from '~/store/GameContext.svelte';
  import { DailyGame } from '~/store/daily.svelte';

  const game = $derived(getGame<DailyGame>().current);

  const TOTAL_SWAPS = $derived(game.totalSwaps);

  // const hintTimer = Timer(5);

  // TODO
  // $: if (hint) {
  //     console.log({ hint });
  //   hintTimer.start(10, () => {
  //     console.log({ hint });
  //     $game.marquee = `H ${hint}`;
  //     hintTimer.start(10, () => {
  //       $game.marquee = undefined;
  //     })
  //   });
  // } else {
  //   hintTimer.pause();
  // }

  // onDestroy(() => {
  //   hintTimer.pause();
  // })
  const [radius, strokeWidth] = $derived(dims.gauge);

  const flooredSwaps = $derived(Math.max(game.swaps, 0));
  // svelte-ignore state_referenced_locally
  let prevSwaps = $state(flooredSwaps || -1);
  let direction = $state<Direction>();

  $effect(() => {
    if (prevSwaps < flooredSwaps) {
      prevSwaps = flooredSwaps;
      direction = 'down';
    } else if (prevSwaps > flooredSwaps) {
      prevSwaps = flooredSwaps;
      direction = 'up';
    }
  });

  const ratio = 2 / 3;
  const innerRadius = $derived(radius - strokeWidth / 2);
  const circumference = $derived(innerRadius * 2 * Math.PI);
  const colors = $derived(
    theme.current === 'dark' ? ['#E04200', '#704270'] : ['#D47792', '#C48BDA'],
  ) as [string, string];
  const gaugeColor = $derived(
    interpolateRgb(...colors)(flooredSwaps / TOTAL_SWAPS),
  );

  const level = $derived(game.gaugeLevel);
  const arc = $derived(circumference * ratio);
  const dashArray = $derived(`${arc} ${circumference}`);
  const ticks = $derived(
    '0 ' +
      Array.from({ length: level })
        .map((_, i) => `${arc / level} 0`)
        .slice(0, 2) // two dots
        .concat([`${circumference}`])
        .join(' '),
  );

  // use to compute exponential score progress
  // svelte-ignore state_referenced_locally
  const tweenedSwaps = new Tween(flooredSwaps, { easing: backOut });
  $effect(() => {
    tweenedSwaps.target = flooredSwaps;
  });

  const getOffset = $derived((arc: number, progress: number) => {
    const progPct = progress / TOTAL_SWAPS;
    return arc - progPct * arc;
  });

  const getHandleRotation = $derived(
    (progress: number) =>
      `rotate(${(360 * ratio * progress) / TOTAL_SWAPS - 120}, ${radius}, ${radius})`,
  );
</script>

<GaugeContainer
  {radius}
  {direction}
  swaps={flooredSwaps}
  marquee={game.marquee}
>
  {#snippet svg()}
    <g transform="rotate(150, {radius}, {radius})">
      <circle
        class="gauge-base"
        cx={radius}
        cy={radius}
        r={innerRadius}
        stroke="gray"
        stroke-width={strokeWidth / 2}
        stroke-dasharray={dashArray}
      />
      <circle
        class="gauge"
        style="--color: {gaugeColor};"
        cx={radius}
        cy={radius}
        r={innerRadius}
        stroke-width={strokeWidth}
        stroke-dasharray={dashArray}
        stroke-dashoffset={getOffset(arc, tweenedSwaps.current)}
      />
      <circle
        class="gauge-ticks"
        cx={radius}
        cy={radius}
        r={innerRadius}
        stroke="white"
        opacity="0.6"
        stroke-width={strokeWidth / 2}
        stroke-dasharray={ticks}
      />
    </g>
    <Handle
      color="gray"
      small
      dy={strokeWidth / 2}
      transform={getHandleRotation(TOTAL_SWAPS)}
    />
    <Handle
      class="gauge-handle lvl-{level}"
      color={gaugeColor}
      dy={strokeWidth / 2}
      transform={getHandleRotation(tweenedSwaps.current)}
    />
    <Handle
      color="white"
      small
      dy={strokeWidth / 2}
      transform={getHandleRotation(tweenedSwaps.current)}
    />
    <Marquee {radius} {innerRadius} marquee={game.marquee} />
  {/snippet}
  {#snippet stars()}
    <Stars {ratio} {radius} color={gaugeColor} />
  {/snippet}
</GaugeContainer>

<style lang="scss">
  @use 'sass:color';
  @use 'sass:list';
  @use '../levels' as *;
  @use '~/theme/include-media.scss' as *;

  circle {
    stroke-linecap: round;
    fill: transparent;
  }

  @for $i from 1 through 5 {
    $color: list.nth($levels, $i);
    .gauge.lvl-#{$i} {
      stroke: var(--color);
    }
    .gauge-handle.lvl-#{$i} {
      fill: var(--color);
    }
  }
  .gauge {
    stroke: var(--color);
  }
  .gauge-new {
    stroke: var(--stroke-color);
  }
</style>
